import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import { useGetUnmatchedInvoicesToClaims, useGetUnmatchedInvoicesToClaimsProducts } from '@/hooks/service-hooks/billingTools';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getUnmatchedInvoicesToClaims } from '@/services/billingTools';
import type {
	BillingToolsProducts,
	UnmatchedInvoicesToClaim,
} from '@/types/billingTools';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { unmatchedInvoicesToClaimsColumns } from '../billingTools';

const CLINIC_KEY = 'Clinic';

/**
 * UnmatchedInvoicestoClaimsTable Component
 *
 * Displays invoices that exist in the system but do not have corresponding claims filed.
 * Helps identify billing gaps where invoices were generated but claims were not filed.
 *
 * Features:
 * - Real-time display of unmatched invoices
 * - Shows product, patient info, clinic, physician, and claim details
 * - Filterable by clinic
 * - Export functionality (CSV, Excel)
 */
const UnmatchedInvoicestoClaimsTable = () => {
	// Shared clinic filter helpers (key used by Unmatched Invoices API)
	const { defaultClinicNames, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);
	const [products, setProducts] = useState<BillingToolsProducts[]>([]);

	// Table filter state: manages pagination, search, sorting, and tab selection
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
					{
						key: CLINIC_KEY,
						values: defaultClinicNames,
					},
				]
				: [],
		),
		search: '',
		enabled: defaultClinicNames.length > 0,
		tab: '',
		sort: '',
	});

	const columns = useMemo(() => unmatchedInvoicesToClaimsColumns, []);

	// State and effect to fetch products for product filter options
	const { data: productsData } = useGetUnmatchedInvoicesToClaimsProducts({
		globalFilters: JSON.stringify([
			{
				key: CLINIC_KEY,
				values: defaultClinicNames,
			},
		]),
		enabled: defaultClinicNames.length > 0 && products.length === 0,
	});

	useEffect(() => {
		if (productsData?.items?.length && !products.length) {
			setProducts(productsData.items);
		}
	}, [products.length, productsData]);

	// Fetch unmatched invoices data based on current filters
	const { data, isFetching } = useGetUnmatchedInvoicesToClaims({
		...filters,
		enabled: JSON.parse(filters.globalFilters || '[]').length > 0,
	});

	/**
	 * Transform unmatched invoices data for table display
	 * Converts raw API data into table-ready format with:
	 * - Formatted procedure dates
	 * - Product and patient information
	 * - Clinic and physician details
	 * - Claim disposition and insurance information
	 */
	const transformedData = useMemo(() => {
		if (!data?.items) return [];
		const unmatchedInvoicesToClaimsData = data.items;

		return unmatchedInvoicesToClaimsData.map((item) => {
			return {
				id: item.TATransactionID,
				EMRID: item.EMRID ? (
					<FaceliftTruncatedTextWithTooltip text={item.EMRID} maxLength={15} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={15} />
				),
				Clinic: item.Clinic ? (
					<FaceliftTruncatedTextWithTooltip text={item.Clinic} maxLength={35} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={35} />
				),
				productid1: item.productid1 ? (
					<FaceliftTruncatedTextWithTooltip text={item.productid1} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				PhysicianName: item.PhysicianName ? (
					<FaceliftTruncatedTextWithTooltip text={item.PhysicianName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				patient: item.patient ? (
					<FaceliftTruncatedTextWithTooltip text={item.patient} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				ProcedureDOS: item.ProcedureDOS ? formatDateToString(new Date(item.ProcedureDOS)) : '--',
				product: item.product ? (
					<FaceliftTruncatedTextWithTooltip text={item.product} maxLength={25} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={25} />
				),
				QCode: item.QCode || '--',
				SumOfUnits: item.SumOfUnits ?? '--',
				claimdisposition: item.claimdisposition ? (
					<FaceliftTruncatedTextWithTooltip text={item.claimdisposition} maxLength={25} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={25} />
				),
				primaryinsurance: item.primaryinsurance ? (
					<FaceliftTruncatedTextWithTooltip text={item.primaryinsurance} maxLength={35} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={35} />
				),
			};
		});
	}, [data]);

	/**
	 * Transform unmatched invoices data for export
	 * Converts data to plain text format for CSV/Excel compatibility
	 */
	const transformedDataToExport = (
		unmatchedInvoicesToClaimsData: UnmatchedInvoicesToClaim[],
	) => {
		if (!unmatchedInvoicesToClaimsData.length) return [];

		return unmatchedInvoicesToClaimsData.map((item) => {
			return {
				id: item.TATransactionID,
				EMRID: item.EMRID || '--',
				Clinic: item.Clinic || '--',
				productid1: item.productid1 || '--',
				PhysicianName: item.PhysicianName || '--',
				patient: item.patient || '--',
				ProcedureDOS: item.ProcedureDOS ? formatDateToString(new Date(item.ProcedureDOS)) : '--',
				product: item.product || '--',
				QCode: item.QCode || '--',
				SumOfUnits: item.SumOfUnits ?? '--',
				claimdisposition: item.claimdisposition || '--',
				primaryinsurance: item.primaryinsurance || '--',
			};
		});
	};

	/**
	 * Handle filter changes from DataTable
	 * Updates filter state to trigger new data fetch with applied filters
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				globalFilters: JSON.stringify(mappedFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters],
	);

	/**
	 * Fetch all unmatched invoices records for export
	 * Iterates through all pages to get complete dataset with current filters applied
	 * @returns Complete unmatched invoices data formatted for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: UnmatchedInvoicesToClaim[] = [];
		let page = 1;
		let hasMore = true;

		while (hasMore) {
			const response = await getUnmatchedInvoicesToClaims(
				page,
				9999,
				filters.search,
				(filters.status as string) || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const unmatchedClaimstoinvoicesDataForExcel = { items: allItems };
		const transformedData = transformedDataToExport(
			unmatchedClaimstoinvoicesDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export unmatched invoices data to CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`UnmatchedInvoicesToClaims-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export unmatched invoices data to Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`UnmatchedInvoicesToClaims-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			<DataTable
				tableName="Unmatched Invoices To Claims"
				loading={isFetching}
				items={transformedData}
				columns={columns}
				topLabel="Unmatched Invoices To Claims"
				totalCount={data?.meta.totalItems || 0}
				onFilterApply={onFilterApply}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				globalFilter={{
					title: 'Filters',
					items: [
						{
							options: defaultClinicNames.map((clinic) => ({
								label: clinic,
								id: clinic,
							})),
							key: 'Clinics',
							name: 'Clinics',
						},
						{
							options: products.map((product) => ({
								label: product.Product,
								id: product.Product,
							})),
							key: 'Product',
							name: 'Products',
						},
					],
				}}
				actions={[]}
				exportable
				hideAllStatusFilter={true}
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>
		</Box>
	);
};

export default UnmatchedInvoicestoClaimsTable;
